import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    Input,
    OnDestroy,
    OnInit,
    signal
} from '@angular/core';

import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, take, tap } from 'rxjs/operators';

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { ShAutocompleteService } from '../../../../core/autocomplete/services/autocomplete.service';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { EmptyStructuredQuery } from '../../../../core/query/entities/empty-structured-query.constant';
import { AdvancedSearch, StructuredQuery } from '../../../../core/query/entities/structured-query.entity';
import { ShUrlQueryService } from '../../../../core/query/services/url-query.service';
import { DialogRef } from '../../../../dynamic-dialog/entities/dialog-ref.class';
import { ShDynamicDialogService } from '../../../../dynamic-dialog/services/dynamic-dialog.service';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import { ActionItem, ActionItemStyle } from '../../../../elements/components/action-item/entities/action-item.entity';
import { DialogStyle } from '../../../../elements/components/dialog/entities/dialog.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { FullTextSearchGuard } from '../../../../modules/guards/full-text-search.guard';
import { SemanticSearchGuard } from '../../../../modules/guards/semantic-search.guard';
import { ShAdvancedSearchDialogContainerComponent } from '../../../advanced-search-dialog/containers/advanced-search-dialog/advanced-search-dialog.container';
import { AdvancedSearchTranslations } from '../../../advanced-search-dialog/entities/i18n/advanced-search-translations.entity';
import { FilterSizingType } from '../../base/entities/filter-sizing-type';
import { QueryPartData } from '../../base/entities/query-part-data.entity';
import { TagSearchFilterStyle } from '../../base/entities/tag-search-filter-style';
import { TagSearchFilter } from '../../components/filter-tag-search/filter-tag-search.class';
import { ShFilterTagSearchComponent } from '../../components/filter-tag-search/filter-tag-search.component';
import { Filter } from '../../entities/filter.class';
import { SearchMode } from '../../entities/search-mode.entity';
import { advancedSearchTranslationsFactory } from '../../helpers/advanced-search-translations-factory.entity';

@Component({
    selector: 'sh-search-bar',
    templateUrl: './search-bar.container.component.html',
    styleUrls: ['./search-bar.container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ShFilterTagSearchComponent,
        NgIf,
        NgFor,
        ShActionItemComponent,
        AsyncPipe,
        ShIconComponent,
        ReactiveFormsModule,
        TranslateModule
    ]
})
export class ShSearchBarContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    public IconSize: typeof IconSize = IconSize;

    // Injectors
    private readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);
    private readonly semanticSearchGuard: SemanticSearchGuard = inject(SemanticSearchGuard);
    private readonly fullTextSearchGuard: FullTextSearchGuard = inject(FullTextSearchGuard);
    private readonly dynamicDialogService: ShDynamicDialogService = inject(ShDynamicDialogService);
    private readonly autocompleteService: ShAutocompleteService = inject(ShAutocompleteService);

    readonly externalActionItems$: BehaviorSubject<ActionItem[]> = new BehaviorSubject<ActionItem[]>([]);
    readonly activeQueryFilter$: ReplaySubject<Filter> = new ReplaySubject<Filter>(1);

    private readonly advancedSearchI18n: AdvancedSearchTranslations = advancedSearchTranslationsFactory(
        this.translateService
    );

    public initialTagSearchFilter$: Observable<Filter>;
    public singleTag: boolean;

    public aiToggleControl: FormControl<boolean> = new FormControl<boolean>(false) as FormControl<boolean>;
    public fullTextToggleControl: FormControl<boolean> = new FormControl<boolean>(false) as FormControl<boolean>;

    public actionItems = input([]);
    public enableSemanticSearch = input(false);
    public enableFullTextSearch = input(false);

    public $aiEnabled = signal(false);
    public $fullTextEnabled = signal(false);

    public $semanticSearchPlugin = toSignal(this.semanticSearchGuard.hasAccess());
    public $showAI = computed(() => this.$semanticSearchPlugin() && this.enableSemanticSearch());

    public $fullTextPlugin = toSignal(this.fullTextSearchGuard.hasAccess());
    public $showFullText = computed(() => this.$fullTextPlugin() && this.enableFullTextSearch());

    $structuredQuery = toSignal(this.urlQueryService.getStructuredQuery(), {
        initialValue: EmptyStructuredQuery
    });

    $searchBarActionItems = computed(() => this.getCombinedActionItems(this.$structuredQuery(), this.actionItems()));

    @Input() enableAdvancedSearch = false;

    @Input() tag?: string;

    public ngOnInit(): void {
        this.initTagSearchFilter();
        this.initAiToggleListener();
        this.initFullTextToggleListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.externalActionItems$.complete();
        this.activeQueryFilter$.complete();
    }

    private initTagSearchFilter() {
        this.initialTagSearchFilter$ = this.urlQueryService.getStructuredQuery().pipe(
            debounceTime(1), // Small debounce so this does not trigger while initial empty query is being restored from state
            tap(structuredQuery => {
                if (structuredQuery.Semantic) {
                    this.aiToggleControl.setValue(true, { emitEvent: true });
                }
                if (structuredQuery.FullText) {
                    this.fullTextToggleControl.setValue(true, { emitEvent: true });
                }
            }),
            map(structuredQuery => {
                const fullTextValue: string = structuredQuery.FullText;
                const semanticValue: string = structuredQuery.Semantic;
                const globalValues: string[] = structuredQuery.Global;

                // Initialize with semantic or fulltext search if value set, else global
                let field: SearchMode = SearchMode.Global;
                let values: string[] = globalValues;

                if (fullTextValue) {
                    field = SearchMode.FullText;
                    values = [fullTextValue];
                }

                if (semanticValue) {
                    field = SearchMode.Semantic;
                    values = [semanticValue];
                }

                const filter: TagSearchFilter = new TagSearchFilter(field);
                filter.placeholderMsg = this.translateService.instant('general.search-here');
                filter.sizing = FilterSizingType.Growable;
                filter.style = TagSearchFilterStyle.SearchActionButton;
                filter.values = values;
                filter.autocompleteFct = this.autocompleteService.getAutocompleteSuggestions.bind(
                    this.autocompleteService
                );

                return filter;
            }),
            shareReplay(1)
        );
    }

    // #region Semantic Search
    private initAiToggleListener(): void {
        this.aiToggleControl.valueChanges.pipe(startWith(false), distinctUntilChanged()).subscribe(enabled => {
            if (enabled) {
                // Can not be active together
                this.fullTextToggleControl.setValue(false);
            }
            this.$aiEnabled.set(enabled);
        });
    }

    // #endregion

    // #region Semantic Search
    private initFullTextToggleListener(): void {
        this.fullTextToggleControl.valueChanges.pipe(startWith(false), distinctUntilChanged()).subscribe(enabled => {
            if (enabled) {
                // Can not be active together
                this.aiToggleControl.setValue(false);
            }

            this.$fullTextEnabled.set(enabled);
        });
    }

    // #endregion
    // #region ActionItems
    protected getCombinedActionItems(
        structuredQuery: StructuredQuery,
        externalActionItems: ActionItem[] | null
    ): ActionItem[] {
        const internalActionItems: ActionItem[] = [];

        if (this.enableAdvancedSearch) {
            const totalTerms: number = this.countAdvancedSearchTerms(structuredQuery.AdvancedSearch);
            const badgeLabel: string | undefined = totalTerms ? totalTerms.toString() : undefined;

            const advancedSearchActionItem: ActionItem = {
                label: this.advancedSearchI18n.action.title,
                eventListener: () => this.openAdvancedSearch(),
                style: ActionItemStyle.Light,
                icon: 'binoculars',
                badgeLabel
            };

            internalActionItems.push(advancedSearchActionItem);
        }

        return [...internalActionItems, ...(externalActionItems ?? [])];
    }

    public openAdvancedSearch(): void {
        this.urlQueryService
            .getStructuredQuery()
            .pipe(take(1))
            .subscribe(structuredQuery => {
                this.openAdvancedSearchDialog(structuredQuery);
            });
    }

    private openAdvancedSearchDialog(structuredQuery: StructuredQuery): void {
        const dialogRef: DialogRef<AdvancedSearch> = this.dynamicDialogService.open<AdvancedSearch>(
            ShAdvancedSearchDialogContainerComponent,
            {
                title: this.advancedSearchI18n.dialog.title,
                options: { style: DialogStyle.ExtraWide },
                data: {
                    i18n: this.advancedSearchI18n.advancedSearch,
                    advancedSearch: structuredQuery.AdvancedSearch,
                    tag: this.tag
                }
            }
        );

        dialogRef.afterClosed$.subscribe(updatedAdvancedSearch =>
            this.onAdvancedSearchChanged(structuredQuery, updatedAdvancedSearch)
        );
    }

    public onAdvancedSearchChanged(oldStructuredQuery: StructuredQuery, advancedSearch: AdvancedSearch): void {
        const updatedQuery: StructuredQuery = {
            ...oldStructuredQuery,
            AdvancedSearch: advancedSearch
        };

        this.urlQueryService.setAdvancedSearchQueryParam(updatedQuery);
    }
    // #endregion

    // #region Helpers
    private countAdvancedSearchTerms(advancedSearch: AdvancedSearch): number {
        let count: number = 0;

        advancedSearch.Groups.forEach(group =>
            (group.Terms ?? []).forEach(term => {
                if (term.Values && term.Values.length > 0) {
                    count += term.Values.length;
                }
                if (term.RangeValues && term.RangeValues.length > 0) {
                    count += term.RangeValues.length;
                }
            })
        );
        return count;
    }
    // #endregion

    // #region Events

    public onSearchBarChange(queryPart: QueryPartData): void {
        const oldStructuredQuery = this.$structuredQuery();

        const isFullTextSearch = queryPart.field === SearchMode.FullText;
        const isSemanticSearch = queryPart.field === SearchMode.Semantic;
        const isGlobalSearch = !isFullTextSearch && !isSemanticSearch;

        const wasEmpty =
            oldStructuredQuery.Semantic === '' &&
            oldStructuredQuery.Global.length === 0 &&
            oldStructuredQuery.FullText === '';

        const changedToSemantic: boolean = oldStructuredQuery.Semantic === '' && isSemanticSearch && !wasEmpty;
        const changedFromSemantic: boolean = oldStructuredQuery.Semantic !== '' && !isSemanticSearch;

        // Empty values if switching to/from semantic
        const newValues = changedToSemantic || changedFromSemantic ? [] : queryPart.values;

        const newStructuredQuery = {
            ...cloneDeep(oldStructuredQuery),
            Global: isGlobalSearch ? newValues : [],
            Semantic: isSemanticSearch && newValues.length > 0 ? newValues[0] : '',
            FullText: isFullTextSearch && newValues.length > 0 ? newValues[0] : ''
        };

        this.urlQueryService.setAdvancedSearchQueryParam(newStructuredQuery);
    }
    // #endregion
}
